import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getClientMenus, getMyDrinks } from '../../services/Clint/UserOrdersApi';
import { getImageUrl } from '../../services/indexMediaBaseUrl';

const MenuItem = ({ item, isProduct = true, onShowDetails }) => {
  const name = isProduct ? item.name : item.drink_name || item.name;
  const imageUrl = getImageUrl(item.image);
  const drinkRatings = !isProduct && Array.isArray(item.ratings) ? item.ratings : [];
  const ratingsCount = drinkRatings.length;
  const averageRate =
    ratingsCount > 0
      ? (drinkRatings.reduce((sum, rating) => sum + Number(rating?.rate || 0), 0) / ratingsCount).toFixed(1)
      : null;
  const latestComment =
    ratingsCount > 0
      ? [...drinkRatings]
          .reverse()
          .find((rating) => rating?.comment && String(rating.comment).trim())?.comment
      : '';

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: 6,
        },
      }}
    >
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt={name}
          sx={{
            width: '100%',
            height: 180,
            objectFit: 'cover',
          }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: 180,
            backgroundColor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isProduct ? (
            <LocalDiningIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
          ) : (
            <RestaurantMenuIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
          )}
        </Box>
      )}

      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1 }}>
            {name}
          </Typography>
        </Box>

        {!isProduct && item.temperature && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            🌡️ {item.temperature}
          </Typography>
        )}

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {item.description || 'بدون وصف'}
        </Typography>

        {!isProduct && (
          <Box sx={{ mb: 1.5 }}>
            {ratingsCount > 0 ? (
              <>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`التقييم: ${averageRate} / 5`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`عدد التقييمات: ${ratingsCount}`}
                  />
                </Box>
                {latestComment ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                    آخر تعليق: {latestComment}
                  </Typography>
                ) : null}
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                لا يوجد تقييمات حتى الآن
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AttachMoneyIcon fontSize="small" color="success" />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
              {item.price}
            </Typography>
          </Box>


        </Box>

        {item.status === 0 && (
          <Chip
            label="غير متاح"
            size="small"
            color="error"
            sx={{ mt: 1 }}
          />
        )}

        {!isProduct && (
          <Button
            variant="text"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => onShowDetails?.(item)}
            sx={{ mt: 1 }}
          >
            عرض كل البيانات
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const isVisibleItem = (item) => Number(item?.status) !== 0;
const isPublicApprovedDrink = (drink) =>
  Number(drink?.is_approved) === 1 &&
  Number(drink?.is_public) === 1 &&
  Number(drink?.status) === 1;

const getCategoryImageUrl = (items = []) => {
  const categoryImage = items[0]?.category?.image;
  const itemImage = items.find((item) => item?.image)?.image;
  return getImageUrl(categoryImage || itemImage);
};

const CategoryCard = ({ categoryName, items, color = 'primary', icon, onClick }) => {
  const description = items[0]?.category?.description || items[0]?.category?.name_en || '';

  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'transform 0.2s, box-shadow 0.2s',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: 6,
          borderColor: `${color}.main`,
        },
      }}
    >
      {getCategoryImageUrl(items) ? (
        <Box
          component="img"
          src={getCategoryImageUrl(items)}
          alt={categoryName}
          sx={{
            width: '100%',
            height: 160,
            objectFit: 'cover',
          }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: 160,
            backgroundColor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      )}

      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }} noWrap>
            {categoryName}
          </Typography>
          <Box sx={{ color: `${color}.main`, display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
        </Box>

        {description ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              minHeight: 40,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {description}
          </Typography>
        ) : null}

        <Chip
          label={`${items.length} عنصر`}
          color={color}
          variant="outlined"
          size="small"
          sx={{ alignSelf: 'flex-start', fontWeight: 'bold', mt: 'auto' }}
        />
      </CardContent>
    </Card>
  );
};

const CategoryHeader = ({ categoryName, color = 'primary', onBack }) => (
  <Paper sx={{ p: 2, mb: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5 }}>
      <Button
        variant="outlined"
        color={color}
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ borderRadius: 2, fontWeight: 'bold' }}
      >
        التصنيفات
      </Button>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }} noWrap>
        {categoryName}
      </Typography>
    </Box>
  </Paper>
);

const Menu = () => {
  const [products, setProducts] = useState([]);
  const [drinks, setDrinks] = useState([]);
  const [myDrinks, setMyDrinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('products');
  const [selectedDrink, setSelectedDrink] = useState(null);
  const [drinkDialogOpen, setDrinkDialogOpen] = useState(false);
  const [selectedProductCategory, setSelectedProductCategory] = useState('');
  const [selectedMyDrinkCategory, setSelectedMyDrinkCategory] = useState('');

  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    setLoading(true);
    setError('');
    try {
      const [menuResponse, myDrinksResponse] = await Promise.all([
        getClientMenus(),
        getMyDrinks(),
      ]);

      setProducts((menuResponse?.data?.products || []).filter(isVisibleItem));
      setDrinks((menuResponse?.data?.drinks || []).filter(isPublicApprovedDrink));
      setMyDrinks(myDrinksResponse?.data?.order || []);
    } catch (err) {
      console.error('خطأ في جلب المنيو:', err);
      setError(err.message || 'حدث خطأ في جلب المنيو');
    } finally {
      setLoading(false);
    }
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredProducts = products.filter((product) => {
    if (!normalizedSearch) return true;

    const fields = [
      product?.name,
      product?.name_en,
      product?.description,
      product?.description_en,
      product?.code,
      product?.category?.name,
      product?.category?.name_en,
    ];

    return fields.some((field) => String(field || '').toLowerCase().includes(normalizedSearch));
  });

  const groupedProducts = filteredProducts.reduce((acc, product) => {
    const categoryName = product?.category?.name || product?.category?.name_en || 'بدون فئة';
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(product);
    return acc;
  }, {});

  const categoryEntries = Object.entries(groupedProducts);

  const filteredDrinks = drinks.filter((drink) => {
    if (!normalizedSearch) return true;

    const fields = [
      drink?.drink_name,
      drink?.name,
      drink?.description,
      drink?.temperature,
      drink?.taste,
      drink?.code,
      drink?.category?.name,
      drink?.category?.name_en,
    ];

    return fields.some((field) => String(field || '').toLowerCase().includes(normalizedSearch));
  });

  const filteredMyDrinks = myDrinks.filter((drink) => {
    if (!normalizedSearch) return true;

    const fields = [
      drink?.drink_name,
      drink?.name,
      drink?.description,
      drink?.temperature,
      drink?.taste,
      drink?.code,
      drink?.approval_notes,
      drink?.category?.name,
      drink?.category?.name_en,
    ];

    return fields.some((field) => String(field || '').toLowerCase().includes(normalizedSearch));
  });

  const groupDrinksByCategory = (items) => items.reduce((acc, drink) => {
    const categoryName = drink?.category?.name || drink?.category?.name_en || 'بدون فئة';
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(drink);
    return acc;
  }, {});

  const groupedMyDrinks = groupDrinksByCategory(filteredMyDrinks);
  const myDrinkCategoryEntries = Object.entries(groupedMyDrinks);

  const handleTabChange = (event, value) => {
    setActiveTab(value);
  };

  const handleOpenDrinkDetails = (drink) => {
    setSelectedDrink(drink);
    setDrinkDialogOpen(true);
  };

  const handleCloseDrinkDetails = () => {
    setDrinkDialogOpen(false);
    setSelectedDrink(null);
  };

  const parseIngredients = (drink) => {
    if (Array.isArray(drink?.ingredients)) return drink.ingredients;

    if (typeof drink?.ingredients === 'string' && drink.ingredients.trim()) {
      try {
        return JSON.parse(drink.ingredients);
      } catch (e) {
        return [];
      }
    }

    return [];
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box dir="rtl" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <RestaurantMenuIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              📋 المنيو
            </Typography>
            <Typography variant="caption" color="text.secondary">
              تصفح كل المنتجات مع التقسيم حسب الفئة
            </Typography>
          </Box>
        </Box>
      </Box>

      <Paper sx={{ p: 1, mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="products" label={`المنتجات (${products.length})`} />
          <Tab value="drinks" label={`مشروبات العملاء (${drinks.length})`} />
          <Tab value="my-drinks" label={`مشروباتي (${myDrinks.length})`} />
        </Tabs>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="ابحث في كل المنتجات (الاسم، الوصف، الكود، الفئة...)"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {activeTab === 'products' && products.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <LocalDiningIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد منتجات
          </Typography>
        </Paper>
      ) : activeTab === 'products' && categoryEntries.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <SearchIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد نتائج مطابقة للبحث
          </Typography>
        </Paper>
      ) : activeTab === 'products' && !selectedProductCategory ? (
        <Grid container spacing={2}>
          {categoryEntries.map(([categoryName, categoryProducts]) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={categoryName}>
              <CategoryCard
                categoryName={categoryName}
                items={categoryProducts}
                color="primary"
                icon={<LocalDiningIcon />}
                onClick={() => setSelectedProductCategory(categoryName)}
              />
            </Grid>
          ))}
        </Grid>
      ) : activeTab === 'products' ? (
        <Box>
          <CategoryHeader
            categoryName={selectedProductCategory}
            color="primary"
            onBack={() => setSelectedProductCategory('')}
          />
          <Grid container spacing={2}>
            {(groupedProducts[selectedProductCategory] || []).map((product) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <MenuItem item={product} isProduct={true} />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : null}

      {activeTab === 'drinks' && drinks.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <RestaurantMenuIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد مشروبات
          </Typography>
        </Paper>
      ) : activeTab === 'drinks' && filteredDrinks.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <SearchIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد نتائج مطابقة للبحث
          </Typography>
        </Paper>
      ) : activeTab === 'drinks' ? (
        <Grid container spacing={2}>
          {filteredDrinks.map((drink) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={drink.id}>
              <MenuItem item={drink} isProduct={false} onShowDetails={handleOpenDrinkDetails} />
            </Grid>
          ))}
        </Grid>
      ) : null}

      {activeTab === 'my-drinks' && myDrinks.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <RestaurantMenuIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد مشروبات خاصة بك
          </Typography>
        </Paper>
      ) : activeTab === 'my-drinks' && myDrinkCategoryEntries.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <SearchIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد نتائج مطابقة للبحث
          </Typography>
        </Paper>
      ) : activeTab === 'my-drinks' && !selectedMyDrinkCategory ? (
        <Grid container spacing={2}>
          {myDrinkCategoryEntries.map(([categoryName, categoryDrinks]) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={categoryName}>
              <CategoryCard
                categoryName={categoryName}
                items={categoryDrinks}
                color="secondary"
                icon={<RestaurantMenuIcon />}
                onClick={() => setSelectedMyDrinkCategory(categoryName)}
              />
            </Grid>
          ))}
        </Grid>
      ) : activeTab === 'my-drinks' ? (
        <Box>
          <CategoryHeader
            categoryName={selectedMyDrinkCategory}
            color="secondary"
            onBack={() => setSelectedMyDrinkCategory('')}
          />
          <Grid container spacing={2}>
            {(groupedMyDrinks[selectedMyDrinkCategory] || []).map((drink) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={drink.id}>
                <MenuItem item={drink} isProduct={false} onShowDetails={handleOpenDrinkDetails} />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : null}

      {false ? (<>
      {activeTab === 'products' && products.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <LocalDiningIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد منتجات
          </Typography>
        </Paper>
      ) : activeTab === 'products' && categoryEntries.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <SearchIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد نتائج مطابقة للبحث
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {activeTab === 'products' && categoryEntries.map(([categoryName, categoryProducts]) => (
            <Paper key={categoryName} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {categoryName}
                </Typography>
                <Chip label={`${categoryProducts.length} منتج`} color="primary" variant="outlined" />
              </Box>

              <Grid container spacing={2}>
                {categoryProducts.map((product) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                    <MenuItem item={product} isProduct={true} />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          ))}

          {activeTab === 'drinks' && (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                اصناف تم صنعها بواسطة العملاء
              </Typography>
              <Chip label={`${filteredDrinks.length} مشروب`} color="warning" variant="outlined" />
            </Box>

            {filteredDrinks.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                <RestaurantMenuIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  لا توجد مشروبات
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {filteredDrinks.map((drink) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={drink.id}>
                    <MenuItem item={drink} isProduct={false} onShowDetails={handleOpenDrinkDetails} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
          )}

          {activeTab === 'my-drinks' && (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                مشروباتي
              </Typography>
              <Chip label={`${filteredMyDrinks.length} مشروب`} color="secondary" variant="outlined" />
            </Box>

            {filteredMyDrinks.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                <RestaurantMenuIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  لا توجد مشروبات خاصة بك
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {filteredMyDrinks.map((drink) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={drink.id}>
                    <MenuItem item={drink} isProduct={false} onShowDetails={handleOpenDrinkDetails} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
          )}
        </Box>
      )}
      </>) : null}

      <Dialog open={drinkDialogOpen} onClose={handleCloseDrinkDetails} fullWidth maxWidth="md">
        <DialogTitle>
          تفاصيل المشروب: {selectedDrink?.drink_name || selectedDrink?.name || '-'}
        </DialogTitle>
        <DialogContent dividers>
          {selectedDrink && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`الكود: ${selectedDrink.code || '-'}`} />
                <Chip label={`السعر: ${selectedDrink.price || '-'} ج`} color="success" />
                <Chip label={`الطعم: ${selectedDrink.taste || '-'}`} color="info" />
                <Chip label={`الحرارة: ${selectedDrink.temperature || '-'}`} color="error" />
                <Chip label={`الحالة: ${Number(selectedDrink.status) === 1 ? 'متاح' : 'غير متاح'}`} />
                <Chip label={`عام: ${Number(selectedDrink.is_public) === 1 ? 'نعم' : 'لا'}`} color="secondary" />
              </Box>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  البيانات الأساسية
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}><Typography variant="body2"><strong>مشروب:</strong> {selectedDrink.drink_name || '-'}</Typography></Grid>
                  <Grid item xs={12} sm={6}><Typography variant="body2"><strong>الاسم المخصص:</strong> {selectedDrink.name || '-'}</Typography></Grid>
                  <Grid item xs={12} sm={6}><Typography variant="body2"><strong>وصف:</strong> {selectedDrink.description || '-'}</Typography></Grid>
                  <Grid item xs={12} sm={6}><Typography variant="body2"><strong>تاريخ انشاء:</strong> {formatDate(selectedDrink.created_at)}</Typography></Grid>
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  المواد ({selectedDrink.materials?.length || 0})
                </Typography>
                {!selectedDrink.materials?.length ? (
                  <Typography variant="body2" color="text.secondary">لا توجد مواد مرتبطة</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedDrink.materials.map((m, idx) => (
                      <Box key={`${m?.id || 'mat'}-${idx}`} sx={{ p: 1, borderRadius: 1, bgcolor: 'grey.100' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {m?.material?.name || '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          الكمية: {m?.quantity || '-'} {m?.material?.measurment_unit?.abbreviation || m?.material?.measurment_unit?.name || '-'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  المكونات (ingredients)
                </Typography>
                {parseIngredients(selectedDrink).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">لا توجد مكونات</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {parseIngredients(selectedDrink).map((ing, idx) => (
                      <Box key={`${ing?.material_id || 'ing'}-${idx}`} sx={{ p: 1, borderRadius: 1, bgcolor: 'grey.100' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {ing?.name || '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          الكمية: {ing?.amount || '-'} {ing?.unit || '-'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  التقييمات ({selectedDrink.ratings?.length || 0})
                </Typography>
                {!selectedDrink.ratings?.length ? (
                  <Typography variant="body2" color="text.secondary">لا توجد تقييمات</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedDrink.ratings.map((rating, idx) => (
                      <Box key={`${rating?.id || 'rate'}-${idx}`} sx={{ p: 1, borderRadius: 1, bgcolor: 'grey.100' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          التقييم: {rating?.rate || 0} / 5
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          المستخدم: {rating?.user?.name || '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          التعليق: {rating?.comment || 'بدون تعليق'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(rating?.created_at)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDrinkDetails}>إغلاق</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Menu;
